import { isAxiosError } from 'axios'

import { api } from '@/api/client'

/**
 * Ficha do ambiente criado (spec 2026-10-09 §8.1). Quem monta é o servidor
 * (`terraform_runner/ficha_do_ambiente.py`) e quem filtra pelo perfil também
 * (`ficha_da_tela.filtrar`): sem `terraform:criar_ambiente` no tenant, `conta`,
 * `rede`, `registro.state_key` e `registro.mr` não vêm. Esta camada só leva o
 * pedido e traduz a recusa em frase.
 */

/** Uma leitura do @DevOps: `nao_conferido` NUNCA é ok — a tela diz isso. */
export interface Leitura {
  estado: 'ok' | 'nao_conferido' | 'sem_aplicacao' | 'erro' | string
  texto?: string
  motivo?: string | null
  valor?: string
  status?: number
  lido_em?: string | null
}

export interface ServicoDaFicha {
  chave: string | null
  nome: string | null
  papel: string | null
  porta: number | null
  host: string | null
  publico: boolean
  exposicao: string | null
  usa_banco: boolean
  origem: string
  dns: Leitura | null
  tls: Leitura | null
  http: Leitura | null
}

export interface BancoDaFicha {
  engine: string
  engine_version?: string | null
  instance_class?: string | null
  storage_gb?: number | null
  multi_az?: boolean | null
  estado: string
  motivo?: string | null
  origem: string
  lido_em?: string | null
}

export interface FichaDoAmbiente {
  stage_key: string
  tenant_id: number
  projeto: string | null
  ambiente: string
  dominio?: string | null
  link: string | null
  lido_em: string | null
  conta?: { account_id: string | null; regiao: string | null; origem: string }
  rede?: {
    cidr: string | null; vpc_id: string | null; estado: string; motivo?: string | null; origem: string
    lido_em?: string | null
  }
  servicos: ServicoDaFicha[]
  banco: BancoDaFicha
  recursos_criados?: number | null
  registro: {
    pedido: number | null
    chamado: number | null
    state_key?: string | null
    mr?: { iid: number; url: string } | null
  }
  pendencias: { chave: string; estado: string; texto: string }[]
  estado_do_pedido: string
  /** Se QUEM OLHA pode refazer as leituras; o servidor decide de novo no clique. */
  pode_conferir: boolean
  documento?: number | null
}

const base = (tenantId: number, stageKey: string) =>
  `/v1/tenants/${tenantId}/ambientes/${encodeURIComponent(stageKey)}/ficha`

export function lerFicha(tenantId: number, stageKey: string): Promise<FichaDoAmbiente> {
  return api.get<FichaDoAmbiente>(base(tenantId, stageKey)).then((r) => r.data)
}

export function conferirFicha(tenantId: number, stageKey: string): Promise<FichaDoAmbiente> {
  return api.post<FichaDoAmbiente>(`${base(tenantId, stageKey)}/conferir`).then((r) => r.data)
}

const FRASES: Record<string, string> = {
  user_not_in_tenant: 'Você não tem acesso a este ambiente.',
  ambiente_sem_pedido_de_criacao: 'Este ambiente não foi criado pelo ConsoleAI, ou o endereço está errado.',
  sem_permissao_criar_ambiente: 'Só quem pode criar ambientes neste tenant confere a ficha de novo.',
  ambiente_nao_entregue: 'O ambiente ainda não foi entregue: a conferência fica para depois da entrega.',
}

const GENERICA = 'Não foi possível concluir. Tente de novo; se persistir, é caso de olhar o log da API.'

export function fraseDaRecusa(erro: unknown): string {
  if (!isAxiosError(erro)) return GENERICA
  const d = (erro.response?.data as { detail?: unknown } | undefined)?.detail
  return (typeof d === 'string' && FRASES[d]) || GENERICA
}
